<div class="page-container" [class.dialog-mode]="isDialogMode">
  <!-- Main Content -->
  <main class="page-main">
    <!-- Page Content -->
    <div class="page-content">
      <!-- Header Section -->
      <div class="page-title-container" [class.hidden]="isDialogMode">
        <app-breadcrumb></app-breadcrumb>
        <h1 class="page-title">Request Details</h1>
        @if (request && !isLoading && !error) {
          <div class="page-title-actions">
            @if (isComparisonPopUpsEnabled) {
              <div class="title-action" (click)="openInFloatingDialog()">
                <mat-icon class="title-action-icon">select_window_2</mat-icon>
                <span class="title-action-label">Open in dialog</span>
              </div>
            }
            @if (canEditRequest()) {
              <button
                class="status-action-link"
                (click)="onEditRequest()"
                matTooltip="Edit request"
                matTooltipPosition="below"
              >
                <mat-icon class="link-icon">edit</mat-icon>
                <span class="link-text">Edit</span>
              </button>
            }
            @if (canManageRequests &&
            (request.status?.code?.toUpperCase() === "PENDING" ||
              request.status?.code?.toUpperCase() === "NEW")) {
              <button
                class="status-action-link"
                (click)="markAsFulfilled()"
                [disabled]="isUpdatingStatus || !canMarkAsFulfilled()"
                [matTooltip]="markAsFulfilledTooltip()"
                matTooltipPosition="below"
              >
                @if (isUpdatingStatus) {
                  <mat-spinner diameter="16" class="link-spinner"></mat-spinner>
                } @else {
                  <mat-icon class="link-icon">check</mat-icon>
                }
                <span class="link-text">Mark as Fulfilled</span>
              </button>
            }
            @if (fulfilledUpdatedInfo) {
              <span class="last-updated-info">{{ fulfilledUpdatedInfo }}</span>
            }
            <span
              class="badge"
              [ngClass]="getStatusClass(request.status?.code)"
            >
              {{ request.status?.name || request.status?.code || "Unknown" }}
            </span>
          </div>
        }
      </div>

      <!-- Loading State -->
      @if (isLoading) {
        <div class="details-layout">
          <mat-card class="request-details-card description-card">
            <h2 class="section-title">Request Description</h2>
            <div class="title-divider"></div>
            <div class="description-section">
              <div class="skeleton-line xl"></div>
              <div class="skeleton-line long"></div>
            </div>
          </mat-card>

          <div class="two-column-layout">
            <mat-card class="request-details-card request-info-card">
              <h2 class="section-title">Request Information</h2>
              <div class="info-divider"></div>
              <div class="request-info-content">
                <div class="info-row">
                  <div class="skeleton-chip"></div>
                  <div class="skeleton-chip"></div>
                </div>
                <div class="info-section">
                  <div class="detail-row">
                    <div class="detail-item">
                      <h3 class="info-label">Start Date</h3>
                      <div class="skeleton-line medium"></div>
                    </div>
                    <div class="detail-item">
                      <h3 class="info-label">End Date</h3>
                      <div class="skeleton-line medium"></div>
                    </div>
                    <div class="detail-item">
                      <h3 class="info-label">Request Type</h3>
                      <div class="skeleton-line short"></div>
                    </div>
                  </div>
                </div>
              </div>
            </mat-card>

            <mat-card class="request-details-card assignment-details-card">
              <h2 class="section-title">Assignment Details</h2>
              <div class="info-divider"></div>
              <div class="resources-list">
                <div class="resource-item">
                  <div class="resource-details">
                    <div class="skeleton-line long"></div>
                    <div class="skeleton-line medium"></div>
                  </div>
                </div>
              </div>
            </mat-card>
          </div>
        </div>
      }

      <!-- Error State -->
      @if (error && !isLoading) {
        <mat-card class="request-details-card">
          <div class="error-state">
            <mat-icon class="error-icon">error</mat-icon>
            <h3>Error Loading Request</h3>
            <p>{{ error }}</p>
            <button mat-raised-button class="retry-button" (click)="retry()">
              <mat-icon>refresh</mat-icon>
              Retry
            </button>
          </div>
        </mat-card>
      }

      <!-- Access Denied / Not Found State -->
      @if (accessDenied && !isLoading) {
        <mat-card class="request-details-card">
          <div class="error-state">
            <mat-icon class="error-icon">lock</mat-icon>
            <h3>Request unavailable</h3>
            <p>
              You may not have access to view requests within this practice
              area.
            </p>
            <button mat-raised-button class="go-back-button" (click)="goBack()">
              <mat-icon>arrow_back</mat-icon>
              Go Back
            </button>
          </div>
        </mat-card>
      }

      <!-- Request Content -->
      @if (request && !isLoading && !error && !accessDenied) {
        <div class="details-layout">
          <!-- Request Description (full width) -->
          <mat-card class="request-details-card description-card">
            <h2 class="section-title">Request Description</h2>
            <div class="title-divider"></div>
            <div class="description-section">
              <p class="info-text">
                {{ request.description || "No description provided" }}
              </p>
            </div>
          </mat-card>

          <!-- Request Information (left) and Assignment Details (right) -->
          <div class="two-column-layout">
            <!-- ===== Request Information ===== -->
            <mat-card class="request-details-card request-info-card">
              <h2 class="section-title">Request Information</h2>
              <div class="info-divider"></div>

              <div class="request-info-content">
                <!-- Practice Areas -->
                @if (request.practiceAreaCodes &&
                request.practiceAreaCodes.length > 0) {
                  <div class="info-item">
                    <h3 class="section-label">Practice Areas</h3>
                    <app-membership-chips
                      [items]="getPracticeAreaLabels(request.practiceAreaCodes)"
                      [maxVisible]="99"
                    />
                  </div>
                }

                <!-- Dates / type -->
                <div class="info-section">
                  <div class="detail-row">
                    <div class="detail-item">
                      <h3 class="info-label">Start Date</h3>
                      <p class="info-text">{{ formatDate(request.startDate) }}</p>
                    </div>
                    <div class="detail-item">
                      <h3 class="info-label">End Date</h3>
                      <span class="info-text">{{
                          formatDate(request.endDate)
                        }}</span>
                      @if (getAssignmentLengthInWeeks(); as weeks) {
                        <span class="detail-subtext"
                        >{{ weeks }} week{{ weeks !== 1 ? "s" : "" }}</span
                        >
                      }
                    </div>
                    <div class="detail-item">
                      <h3 class="info-label">Request Type</h3>
                      <p class="info-text">
                        {{
                          request.requestType?.name ||
                          request.requestType?.code ||
                          "Unknown"
                        }}
                      </p>
                    </div>
                    <div class="detail-item">
                      <h3 class="info-label">Submitted Date</h3>
                      <p class="info-text">
                        {{ formatDate(request.dateSubmitted) }}
                      </p>
                    </div>
                    @if (request.submittedByUserName) {
                      <div class="detail-item">
                        <h3 class="info-label">Submitted By</h3>
                        <p class="info-text">{{ request.submittedByUserName }}</p>
                      </div>
                    }
                  </div>
                </div>

                <!-- Requested resources (fulfilment) -->
                @if (requestedTotal > 0) {
                  <div class="info-item requested-resources-block">
                    <h3 class="section-label">
                      Resources Requested:
                      <span
                        class="requested-total"
                        [class.met]="fulfilledTotal >= requestedTotal"
                      >{{ fulfilledTotal }}/{{ requestedTotal }}</span
                      >
                    </h3>
                    <div class="fulfillment-pills">
                      @for (row of fulfillmentBreakdown;
                        track row.seniorityCode) {
                        <span
                          class="fulfillment-pill"
                          [class.met]="row.fulfilled >= row.requested"
                        >
                          <span class="pill-label">{{ row.label }}</span>
                          <span class="pill-count"
                          >{{ row.fulfilled }}/{{ row.requested }}</span
                          >
                          @if (row.fulfilled >= row.requested) {
                            <mat-icon class="pill-check">check_circle</mat-icon>
                          }
                        </span>
                      }
                    </div>
                  </div>
                }

                <!-- Requestor + Preferred Resources -->
                <div class="combined-info-row">
                  <!-- Requestor(s) -->
                  <div class="info-item">
                    <h3 class="section-label">Requestor</h3>
                    @if (request.requestors && request.requestors.length > 0) {
                      @for (requestor of request.requestors;
                        track requestor.employeeId) {
                        <div
                          class="resource-item"
                          [class.clickable]="requestor.employeeId"
                          (click)="
                            requestor.employeeId &&
                              navigateToResource(requestor.employeeId)
                          "
                        >
                          <div class="resource-details">
                            <span class="resource-name-requestor">{{
                                requestor.fullName ||
                                (
                                  (requestor.firstName || "") +
                                  " " +
                                  (requestor.lastName || "")
                                ).trim() ||
                                requestor.employeeId
                              }}</span>
                            @if (requestor.email) {
                              <span class="resource-email">{{
                                  requestor.email
                                }}</span>
                            }
                            @if (requestor.title || requestor.seniority) {
                              <span class="resource-title">{{
                                  resourceSubline(
                                    requestor.title,
                                    requestor.seniority
                                  )
                                }}</span>
                            }
                          </div>
                        </div>
                      }
                    } @else {
                      <p class="no-data">No requestor specified</p>
                    }
                  </div>

                  <!-- Preferred Resources -->
                  <div class="info-item">
                    <h3 class="section-label">Preferred Resources</h3>
                    @if (request.preferredEmployees &&
                    request.preferredEmployees.length > 0) {
                      @for (resource of request.preferredEmployees;
                        track getResourceEmployeeId(resource) || $index) {
                        @let pid = getResourceEmployeeId(resource);
                        @let pviewable =
                          !!pid &&
                          !!(resource["canViewResource"] ?? resource["CanViewResource"]);
                        <div
                          class="resource-item"
                          [class.clickable]="pviewable"
                          (click)="pviewable && navigateToResource(pid!)"
                        >
                          <div class="resource-details">
                            @let pemail = resource["email"] || resource["Email"];
                            @let ptitle = resource["title"] || resource["Title"];
                            @let pseniority =
                              resource["seniority"] || resource["Seniority"];
                            <span
                              class="resource-name-requestor"
                              [class.not-clickable]="!pviewable"
                            >{{ getResourceName(resource) }}</span>
                            @if (pemail) {
                              <span class="resource-email">{{
                                  pemail
                                }}</span>
                            }
                            @if (ptitle || pseniority) {
                              <span class="resource-title">{{
                                  resourceSubline(ptitle, pseniority)
                                }}</span>
                            }
                          </div>
                          @if (isPreferredAssigned(resource)) {
                            <span class="preferred-added">
                              <mat-icon>check</mat-icon>
                              Added
                            </span>
                          }
                        </div>
                      }
                    } @else {
                      <p class="no-data">No preferred resources specified</p>
                    }
                  </div>
                </div>
              </div>
            </mat-card>

            <!-- ===== Assignment Details ===== -->
            <mat-card class="request-details-card assignment-details-card">
              <h2 class="section-title">Assignment Details</h2>
              <div class="info-divider"></div>

              @if (canManageRequests) {
                <h3 class="section-label">Client / Matter</h3>
                @let matters = getDisplayMatters();
                @if (matters.length === 0) {
                  <p class="no-data">No client/matter associated</p>
                } @else {
                  <div class="matter-assignment-list">
                    @for (matterId of matters; track matterId; let first = $first) {
                      @let info = matterInfoMap.get(matterId);
                      <div
                        class="matter-assignment-group"
                        [class.open]="isMatterExpanded(matterId)"
                      >
                        <div
                          class="matter-assignment-header"
                          role="button"
                          tabindex="0"
                          [attr.aria-expanded]="isMatterExpanded(matterId)"
                          (click)="toggleMatter(matterId)"
                          (keydown.enter)="toggleMatter(matterId)"
                          (keydown.space)="
                            $event.preventDefault(); toggleMatter(matterId)
                          "
                        >
                          <div class="matter-head-main">
                            @let clientName =
                              info?.clientName ??
                              (first ? request.clientName : null);
                            @if (clientName) {
                              <span class="matter-client-name">{{
                                  clientName
                                }}</span>
                            }
                            <button
                              type="button"
                              class="matter-name-link"
                              (click)="
                                $event.stopPropagation();
                                navigateToMatterById(matterId)
                              "
                            >
                              {{
                                info?.matterName ??
                                (first ? request.matterTitle : null) ??
                                matterId
                              }}
                            </button>
                            <span class="matter-number">{{ matterId }}</span>
                          </div>
                          <div class="matter-head-meta">
                            @if (matterRequestedSummary(matterId)) {
                              <span class="matter-requested-summary"
                              >Requested:
                                {{ matterRequestedSummary(matterId) }}</span
                              >
                            }
                            <span class="matter-assigned-count"
                            >{{ assignedCountForMatter(matterId) }}
                              assigned</span
                            >
                            <mat-icon class="expand-icon">chevron_right</mat-icon>
                          </div>
                        </div>

                        @if (isMatterExpanded(matterId)) {
                          <div class="matter-assignment-body" @expandCollapse>
                            @if (assignedCountForMatter(matterId) === 0) {
                              <div class="matter-no-assignments">
                                No resources assigned to this matter yet.
                              </div>
                            } @else {
                              <div class="assigned-table">
                                <div class="assigned-table-grid">
                                  <div class="assigned-table-header">
                                  <span class="col-resources"
                                  >ASSIGNED RESOURCES</span
                                  >
                                    <span class="col-type">ASSIGNMENT TYPE</span>
                                    <span class="col-practice"
                                    >PRACTICE AREAS</span
                                    >
                                  </div>
                                  @for (resource of pagedAssignedForMatter(matterId);
                                    track resource.employeeId) {
                                    <div class="assigned-table-row">
                                      <div class="col-resources resource-info">
                                      <span class="resource-name-line">
                                        @if (resource.canViewResource) {
                                          <a
                                            class="resource-name resource-link"
                                            (click)="
                                              navigateToResource(
                                                resource.employeeId
                                              )
                                            "
                                          >{{ resource.fullName }}</a
                                          >
                                        } @else {
                                          <span class="resource-name">{{
                                              resource.fullName
                                            }}</span>
                                        }
                                        @if (isAssignedResourcePreferred(resource)) {
                                          <span class="preferred-tag">(preferred)</span>
                                        }
                                      </span>
                                        @if (resource.email) {
                                          <span class="resource-email">{{
                                              resource.email
                                            }}</span>
                                        }
                                        @if (resource.title || resource.seniority) {
                                          <span class="resource-subline">{{
                                              resourceSubline(resource.title, resource.seniority)
                                            }}</span>
                                        }
                                      </div>
                                      <div class="col-type">
                                      <span
                                        class="assignment-type-badge"
                                        [class.primary]="
                                          resource.assignmentTypeCategory ===
                                          'PRIMARY'
                                        "
                                        [class.coverage]="
                                          resource.assignmentTypeCategory ===
                                          'COVERAGE'
                                        "
                                      >
                                        {{
                                          resource.assignmentTypeCategoryName ||
                                          "Primary"
                                        }}
                                      </span>
                                      </div>
                                      <div class="col-practice">
                                        @if (resource.practiceAreaCodes &&
                                        resource.practiceAreaCodes.length > 0) {
                                          <app-membership-chips
                                            [items]="
                                            $any(getAssignedResourcePracticeAreas(resource))
                                          "
                                            labelKey="description"
                                          />
                                        } @else {
                                          <span class="text-muted">—</span>
                                        }
                                      </div>
                                    </div>
                                  }
                                </div>

                                @if (assignedCountForMatter(matterId) > 0) {
                                  <mat-paginator
                                    [length]="assignedCountForMatter(matterId)"
                                    [pageSize]="matterPageSize(matterId)"
                                    [pageIndex]="matterPageIndex(matterId)"
                                    [pageSizeOptions]="[10, 25, 50]"
                                    (page)="onMatterPageChange(matterId, $event)"
                                    showFirstLastButtons="true"
                                    class="resource-list-paginator"
                                  >
                                  </mat-paginator>
                                }
                              </div>
                            }
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              } @else {
                <div class="assignment-details">
                  <p class="placeholder-message">
                    Resource assignments will be displayed here
                  </p>
                </div>
              }
            </mat-card>
          </div>
        </div>
      }
    </div>
  </main>
</div>
